/**
 * Flow Lifecycle Registry — per-hero declarative configuration.
 *
 * One entry per FlowId. The shared `<FlowLifecycleManager>` reads
 * from here for the on-demand confirm modals (Restart / Discard /
 * Leave). Hosts contribute nothing more than wiring the manager into
 * their JSX tree. Adding a new hero with a CoPilotGuide is a
 * registry edit + a host wrap — no new modals, no new hooks.
 *
 * Retake is owned by the HOST PAGE via `<HeroRetakeModal>` (see
 * CheckUpPage / QultureUpPage results views). The registry no longer
 * carries retake config — copy lives at the call site.
 *
 * The auto-opening Resume modal was removed in 2026-04-27. The
 * registry no longer carries `resume` config; the engine restores
 * chat history directly and the user lands on their conversation.
 *
 * i18n: every label/title/body uses the `copilot.lifecycle.*` key
 * tree shipped in `packages/locale/src/locales/en.json`. The modal
 * components pass `{ hero, progress, currentStep, totalSteps }` to
 * `t()` for interpolation. There is no English fallback: a missing key
 * renders as the raw key, so the gap is seen and fixed (root CLAUDE.md
 * rule 3) — the fallbacks this registry carried hid one for good
 * (`copilot.lifecycle.discard.confirmTeleportalSetup` existed in no locale).
 */

import type { FlowLifecycleConfig, FlowId } from './types';

// ─────────────────────────────────────────────────────────────────
// Shared modal factories — same copy for every hero except
// {{hero}} interpolation at render time.
// ─────────────────────────────────────────────────────────────────

const sharedLeave: FlowLifecycleConfig['leave'] = {
  titleKey: 'copilot.lifecycle.leave.title',
  bodyKey: 'copilot.lifecycle.leave.body',
  confirmLabelKey: 'copilot.lifecycle.leave.confirm',
  cancelLabelKey: 'copilot.lifecycle.stay',
  // Leave is non-destructive (progress is saved). No arming delay.
  arming: false,
  intent: 'info',
  reversible: true,
  keep: [
    {
      labelKey: 'copilot.lifecycle.leave.keep.progress',
    },
    {
      labelKey: 'copilot.lifecycle.leave.keep.answers',
    },
  ],
  lose: [],
};

const sharedDiscard: FlowLifecycleConfig['discard'] = {
  titleKey: 'copilot.lifecycle.discard.title',
  bodyKey: 'copilot.lifecycle.discard.body',
  confirmLabelKey: 'copilot.lifecycle.discard.confirm',
  cancelLabelKey: 'copilot.lifecycle.stay',
  arming: true,
  intent: 'destructive',
  reversible: false,
  keep: [
    {
      labelKey: 'copilot.lifecycle.discard.keep.history',
    },
  ],
  lose: [
    {
      labelKey: 'copilot.lifecycle.discard.lose.answers',
    },
    {
      labelKey: 'copilot.lifecycle.discard.lose.position',
    },
  ],
};

const sharedRestart: FlowLifecycleConfig['restart'] = {
  titleKey: 'copilot.lifecycle.restart.title',
  bodyKey: 'copilot.lifecycle.restart.body',
  confirmLabelKey: 'copilot.lifecycle.restart.confirm',
  cancelLabelKey: 'copilot.lifecycle.stay',
  arming: true,
  intent: 'warning',
  reversible: false,
  keep: [
    {
      labelKey: 'copilot.lifecycle.restart.keep.history',
    },
  ],
  lose: [
    {
      labelKey: 'copilot.lifecycle.restart.lose.answers',
    },
    {
      labelKey: 'copilot.lifecycle.restart.lose.position',
    },
  ],
};

// ─────────────────────────────────────────────────────────────────
// Per-flow entries — assessment heroes share the same modal copy;
// setup flows override.
// ─────────────────────────────────────────────────────────────────

const checkup: FlowLifecycleConfig = {
  flowId: 'checkup',
  hero: 'checkup',
  restart: sharedRestart,
  discard: sharedDiscard,
  leave: sharedLeave,
};

const careAssessment: FlowLifecycleConfig = {
  flowId: 'care_assessment',
  hero: 'care_assessment',
  restart: sharedRestart,
  discard: sharedDiscard,
  leave: sharedLeave,
};

const qultureup: FlowLifecycleConfig = {
  flowId: 'qultureup',
  hero: 'qultureup',
  restart: sharedRestart,
  discard: sharedDiscard,
  leave: sharedLeave,
};

// ─────────────────────────────────────────────────────────────────
// Setup flows — different shape (gentler "Skip" framing for discard,
// stronger acknowledge gate on restart since it deletes live tasks).
// ─────────────────────────────────────────────────────────────────

const teleportalSetup: FlowLifecycleConfig = {
  flowId: 'teleportal_setup',
  hero: 'teleportal_setup',
  restart: {
    ...sharedRestart,
    titleKey: 'copilot.lifecycle.restart.titleTeleportalSetup',
    bodyKey: 'copilot.lifecycle.restart.bodyTeleportalSetup',
    // High blast radius: deletes live tasks + uploaded docs. Stronger
    // than the assessment-flow restarts → keeps the acknowledge
    // checkbox AND uses destructive intent (overrides shared `warning`).
    intent: 'destructive',
    reversible: false,
    keep: [
      {
        labelKey: 'teleportal.retakeSetup.preFilled',
      },
    ],
    lose: [
      {
        labelKey: 'teleportal.retakeSetup.cleanTasks',
      },
      {
        labelKey: 'teleportal.retakeSetup.cleanDocs',
      },
      {
        labelKey: 'teleportal.retakeSetup.rebuildTasks',
      },
    ],
    acknowledgeRequired: true,
    acknowledgeLabelKey: 'teleportal.retakeSetup.acknowledge',
    confirmLabelKey: 'teleportal.retakeSetup.confirmReset',
    cancelLabelKey: 'copilot.lifecycle.stay',
  },
  discard: {
    ...sharedDiscard,
    titleKey: 'copilot.lifecycle.discard.titleTeleportalSetup',
    bodyKey: 'copilot.lifecycle.discard.bodyTeleportalSetup',
    confirmLabelKey: 'copilot.lifecycle.discard.confirmTeleportalSetup',
    cancelLabelKey: 'copilot.lifecycle.stay',
    // Inherit `arming: true` and `intent: 'destructive'` from
    // sharedDiscard — the modal is destructive of in-flight work, even
    // if the previously-completed kit is preserved. The "info" framing
    // was the source of the user-confusion bug.
    keep: [
      {
        labelKey: 'copilot.lifecycle.discard.keep.teleportalTasks',
      },
    ],
    lose: [
      {
        labelKey: 'copilot.lifecycle.discard.lose.teleportalDraft',
      },
      {
        labelKey: 'copilot.lifecycle.discard.lose.teleportalPosition',
      },
    ],
  },
  leave: sharedLeave,
};

const journeyBuilder: FlowLifecycleConfig = {
  flowId: 'journey_builder',
  hero: 'journey_builder',
  restart: {
    ...sharedRestart,
    titleKey: 'copilot.lifecycle.restart.titleJourneyBuilder',
    bodyKey: 'copilot.lifecycle.restart.bodyJourneyBuilder',
    // Builder has no completed result to keep — clear the keep column.
    keep: [],
    lose: [
      {
        labelKey: 'copilot.lifecycle.restart.lose.builderAnswers',
      },
    ],
  },
  discard: sharedDiscard,
  leave: sharedLeave,
};

export const flowLifecycleRegistry: Record<FlowId, FlowLifecycleConfig> = {
  checkup,
  care_assessment: careAssessment,
  qultureup,
  teleportal_setup: teleportalSetup,
  journey_builder: journeyBuilder,
};
